import { Box, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React from "react";
import { useTranslation } from "react-i18next";
import { JobAd, JobAdIntegrationType, PlatFormStatus } from "../types/common";
import PublishCheckBox from "./PublishCheckBox";
import { ReactComponent as ChatIcon } from "../../../common/icons/chatBubble.svg";
import { ReactComponent as LanguageIcon } from "../../../common/icons/language.svg";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface Props {
  isOpen: boolean;
  toggleOpen: () => void;
  title: string;
  isEditing?: boolean;
  integrationType?: JobAdIntegrationType;
  forcePublish?: boolean;
  onPublishSelection?: (integration: JobAdIntegrationType) => void;
  integrationIsSelectedToBePublished?: boolean;
  selectedJobAd?: JobAd | undefined;
  publishLabel?: string;
}

const FormSectionHeader = ({
  isOpen,
  toggleOpen,
  title,
  integrationType,
  forcePublish,
  onPublishSelection,
  integrationIsSelectedToBePublished,
  isEditing,
  selectedJobAd,
}: Props) => {
  const { t } = useTranslation();

  const integration = selectedJobAd?.integrations?.find(
    (i) => i.integration.type === integrationType,
  )?.integration;

  const getIconForStatus = (status: PlatFormStatus | undefined) => {
    switch (status) {
      case PlatFormStatus.PENDING:
        return <ChatIcon />;
      case PlatFormStatus.PUBLISHED:
        return <LanguageIcon />;
      case PlatFormStatus.FAILED_TO_PUBLISH:
      case PlatFormStatus.FAILED_TO_UPDATE:
      case PlatFormStatus.FAILED_TO_DELETE:
        return <ErrorOutlineIcon sx={{ color: "error.main" }} />;
      default:
        return null;
    }
  };

  const getTextForStatus = (status: PlatFormStatus) => {
    switch (status) {
      case PlatFormStatus.PENDING:
        return (
          <Typography sx={{ opacity: "50%" }}>
            {t(`jobAds:platFormStatus.PENDING`)}
          </Typography>
        );
      case PlatFormStatus.PUBLISHED:
        return <Typography>{t(`jobAds:platFormStatus.PUBLISHED`)}</Typography>;
      case PlatFormStatus.FAILED_TO_PUBLISH:
        return (
          <Typography color="error.main">
            {t(`jobAds:platFormStatus.FAILED_TO_PUBLISH`)}
          </Typography>
        );
      case PlatFormStatus.FAILED_TO_UPDATE:
        return (
          <Typography color="error.main">
            {t(`jobAds:platFormStatus.FAILED_TO_UPDATE`)}
          </Typography>
        );
      case PlatFormStatus.FAILED_TO_DELETE:
        return (
          <Typography color="error.main">
            {t(`jobAds:platFormStatus.FAILED_TO_DELETE`)}
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex" }} alignItems={"center"} width={"100%"}>
        <IconButton
          size="small"
          onClick={toggleOpen}
          sx={{
            padding: 0,
            margin: 0,
            marginLeft: 1,
            marginRight: 2,
            color: "#4A1D9C",
          }}
        >
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography variant="header" sx={{ fontWeight: 600 }}>
          {title.toUpperCase()}
        </Typography>

        {isEditing ? (
          <PublishCheckBox
            label={t(`jobAds:publishOn${title}`)}
            integrationType={integrationType}
            onPublishSelection={onPublishSelection}
            disabled={
              forcePublish ||
              // If the integration is already published, dont allow unpublishing single integration (use close jobAd instead)
              integration?.status === PlatFormStatus.PUBLISHED
            }
            isSelected={integrationIsSelectedToBePublished}
          ></PublishCheckBox>
        ) : (
          integration?.status && (
            <Box
              display="flex"
              columnGap={"8px"}
              alignItems="center"
              sx={{ marginLeft: "auto" }}
            >
              {getIconForStatus(integration?.status)}
              {getTextForStatus(integration?.status)}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default FormSectionHeader;
